import { Box, Button, CircularProgress, Popover, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { VendorFinderDialog } from '@/components/home/VendorFinderDialog';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useVendors } from '@/hooks/useVendors';
import { UserProperty } from '@/models/Property';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import { Vendor, VendorTrade, VENDOR_TRADE_LABELS } from '@/models/Vendor';
import { createRFQShareToken } from '@/services/api/rfqs';
import { updateVendor as updateVendorRecord } from '@/services/api/vendors';

type OutreachMethod = 'sms';

interface RfqVendorOutreachPanelProps {
  userProperty: UserProperty;
  rfqs: RequestForQuotation[];
  issuer?: {
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  } | null;
  suggestedTrades?: string[];
  disabled?: boolean;
  hasRequiredFields: boolean;
  onBeforeOutreach: () => Promise<void>;
  placement?: 'content' | 'header';
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '');

const normalizeContactName = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.toLowerCase() === 'homeowner') return '';
  return trimmed;
};

const tradeLookup = Object.entries(VENDOR_TRADE_LABELS).reduce<Record<string, VendorTrade>>(
  (acc, [trade, label]) => {
    acc[normalize(trade)] = trade as VendorTrade;
    acc[normalize(label)] = trade as VendorTrade;
    return acc;
  },
  {
    [normalize('HVAC SYSTEMS')]: 'air_conditioning_and_heat',
    [normalize('HVAC & Systems')]: 'air_conditioning_and_heat',
    [normalize('HVAC & Air')]: 'air_conditioning_and_heat',
    [normalize('HVAC')]: 'air_conditioning_and_heat',
    [normalize('Air Conditioning')]: 'air_conditioning_and_heat',
    [normalize('Air Conditioner')]: 'air_conditioning_and_heat',
    [normalize('Air Conditioning and Heating')]: 'air_conditioning_and_heat',
    [normalize('Heating and Air Conditioning')]: 'air_conditioning_and_heat',
    [normalize('A/C')]: 'air_conditioning_and_heat',
    [normalize('AC')]: 'air_conditioning_and_heat',
    [normalize('Mold')]: 'mold_and_water_damage',
    [normalize('Mold Remediation')]: 'mold_and_water_damage',
    [normalize('Water Damage')]: 'mold_and_water_damage',
    [normalize('Water Damage Restoration')]: 'mold_and_water_damage',
    [normalize('Water Mitigation')]: 'mold_and_water_damage',
    [normalize('Moisture Intrusion')]: 'mold_and_water_damage',
    [normalize('Restoration')]: 'mold_and_water_damage',
    [normalize('Remediation')]: 'mold_and_water_damage',
    [normalize('EXTERIOR')]: 'general_contracting',
    [normalize('INTERIOR')]: 'handyman',
    [normalize('STRUCTURAL')]: 'structural',
    [normalize('PLUMBING')]: 'plumbing',
    [normalize('ELECTRICAL')]: 'electrical',
  },
);

const inferTrade = (value: string): VendorTrade | null => {
  const normalized = normalize(value);
  const direct = tradeLookup[normalized];
  if (direct) return direct;
  if (
    /hvac|aircondition|airconditioning|acunit|furnace|heatpump|condenser|compressor|duct|thermostat/.test(
      normalized,
    )
  ) {
    return 'air_conditioning_and_heat';
  }
  if (/mold|waterdamage|waterintrusion|watermitigation|moisture|remediation|restoration/.test(normalized)) {
    return 'mold_and_water_damage';
  }
  if (/electric|electrical|wiring|outlet|gfci|breaker|panel|grounding|bonding/.test(normalized)) {
    return 'electrical';
  }
  if (/plumb|pipe|drain|supplyline|sewer|waterheater|faucet|toilet/.test(normalized)) {
    return 'plumbing';
  }
  if (/roof|flashing|shingle|gutter|chimney/.test(normalized)) return 'roofing';
  return null;
};

const mapsHrefFor = (vendor: Vendor) => {
  if (vendor.googleMapsUri) return vendor.googleMapsUri;
  if (vendor.place_id) return `https://www.google.com/maps/place/?q=place_id:${vendor.place_id}`;
  if (vendor.address)
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`;
  return null;
};

const smsPhoneFor = (vendor: Vendor) => vendor.internationalPhoneNumber || vendor.phone || '';

const truncateSmsText = (value: string, maxLength: number) => {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  const trimmed = normalized.slice(0, maxLength).trimEnd();
  const lastSpace = trimmed.lastIndexOf(' ');
  return `${(lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed).trimEnd()}...`;
};

const toSmsSentence = (value?: string) => {
  const normalized = value?.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  return /[.!?]$/.test(normalized) ? normalized : `${normalized}.`;
};

const buildScopeSummary = (rfq: RequestForQuotation) => {
  const objective = toSmsSentence(rfq.objective);
  if (objective) return truncateSmsText(objective, 190);
  const scope = toSmsSentence(rfq.scopeOfWork.find((item) => item.trim().length > 0));
  return [scope]
    .filter(Boolean)
    .map((sentence) => truncateSmsText(sentence as string, 190))
    .join(' ');
};

const formatSmsDate = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const submissionDeadlineFor = (rfq: RequestForQuotation) =>
  formatSmsDate(
    new Date(rfq.createdAt + (rfq.submissionRequirements.dueInDays ?? 0) * 86400000),
  );

const vendorMeta = (vendor: Vendor) =>
  [
    vendor.rating != null ? vendor.rating.toFixed(1) : null,
    vendor.reviewCount != null ? `${vendor.reviewCount.toLocaleString()} reviews` : null,
    vendor.distance ? `${vendor.distance.value.toFixed(1)} ${vendor.distance.unit}` : null,
  ].filter(Boolean);

function GoogleBadge() {
  return (
    <Box
      component="span"
      title="Listed on Google"
      aria-label="Listed on Google"
      sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      <svg width="12" height="12" viewBox="0 0 48 48" aria-hidden="true">
        <path
          fill="#FBBC04"
          d="M9.83 24c0-1.52.25-2.98.7-4.36L2.62 13.6C1.08 16.73.2 20.26.2 24s.88 7.27 2.42 10.4l7.91-6.05c-.45-1.38-.7-2.83-.7-4.35z"
        />
        <path
          fill="#EA4335"
          d="M23.7 10.13c3.31 0 6.3 1.17 8.65 3.09L39.21 6.45C35.05 2.85 29.7.59 23.7.59c-9.36 0-17.4 5.35-21.08 13.01l7.91 6.04C12.33 14.04 17.55 10.13 23.7 10.13z"
        />
        <path
          fill="#34A853"
          d={[
            'M23.7 37.87c-6.15 0-11.37-3.91-13.17-9.52',
            'l-7.91 6.04c3.68 7.66 11.72 13.01 21.08 13.01',
            '5.74 0 11.22-2.04 15.34-5.86l-7.5-5.81',
            'c-2.13 1.34-4.83 2.14-7.84 2.14z',
          ].join(' ')}
        />
        <path
          fill="#4285F4"
          d="M46.98 24c0-1.36-.21-2.81-.53-4.17H23.7v8.85h13.09c-.65 3.22-2.45 5.69-5.04 7.39l7.5 5.81C43.6 37.7 46.98 31.45 46.98 24z"
        />
      </svg>
    </Box>
  );
}

function DirectoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M1 13c0-2.2 2.2-4 5-4s5 1.8 5 4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M12 8a2.5 2.5 0 000-5M15 13c0-1.7-1.3-3.1-3-3.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function RfqVendorOutreachPanel({
  userProperty,
  rfqs,
  issuer,
  suggestedTrades = [],
  disabled = false,
  hasRequiredFields,
  onBeforeOutreach,
  placement = 'content',
}: RfqVendorOutreachPanelProps) {
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const propertyId = userProperty.property.id;
  const { vendors, isFetchingVendors } = useVendors(propertyId);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [hasTouchedSelection, setHasTouchedSelection] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [busyMethod, setBusyMethod] = useState<OutreachMethod | null>(null);
  const [findingTrade, setFindingTrade] = useState<VendorTrade | null>(null);
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState<null | HTMLElement>(null);

  const suggestedTradeSet = useMemo(() => {
    const suggestedMapped = suggestedTrades
      .map((trade) => inferTrade(trade))
      .filter(Boolean) as VendorTrade[];
    if (suggestedMapped.length > 0) return new Set(suggestedMapped);
    const rfqSignals = rfqs.flatMap((rfq) => [rfq.projectTitle, rfq.objective, ...rfq.scopeOfWork]);
    const rfqMapped = rfqSignals.map((trade) => inferTrade(trade)).filter(Boolean) as VendorTrade[];
    return new Set(rfqMapped);
  }, [rfqs, suggestedTrades]);
  const finderTrade = useMemo(
    () => Array.from(suggestedTradeSet)[0] ?? 'general_contracting',
    [suggestedTradeSet],
  );

  const sortedVendors = useMemo(() => {
    const pinned = vendors.filter((v) => v.pinned);
    const scoped =
      suggestedTradeSet.size > 0
        ? pinned.filter((vendor) => suggestedTradeSet.has(vendor.trade))
        : [];
    return scoped.slice().sort((a, b) => {
      if ((a.lastContactedAt ?? 0) !== (b.lastContactedAt ?? 0)) {
        return (b.lastContactedAt ?? 0) - (a.lastContactedAt ?? 0);
      }
      return a.name.localeCompare(b.name);
    });
  }, [suggestedTradeSet, vendors]);

  const recommendedVendorId = useMemo(
    () => sortedVendors.find((v) => smsPhoneFor(v) || mapsHrefFor(v) || v.email)?.id ?? null,
    [sortedVendors],
  );

  useEffect(() => {
    if (hasTouchedSelection || !recommendedVendorId) return;
    setSelectedVendorId(recommendedVendorId);
  }, [hasTouchedSelection, recommendedVendorId]);

  useEffect(() => {
    if (!selectedVendorId) return;
    if (sortedVendors.some((vendor) => vendor.id === selectedVendorId)) return;
    setSelectedVendorId(recommendedVendorId);
    setHasTouchedSelection(false);
  }, [recommendedVendorId, selectedVendorId, sortedVendors]);

  const selectedVendor = useMemo(
    () => sortedVendors.find((vendor) => vendor.id === selectedVendorId) ?? null,
    [selectedVendorId, sortedVendors],
  );

  const propertyAddress = [
    userProperty.property.address,
    userProperty.property.city,
    userProperty.property.state,
    userProperty.property.zipCode,
  ]
    .filter(Boolean)
    .join(', ');

  const buildMessage = (url: string) => {
    const lead = rfqs[0];
    const leadScopeSummary = buildScopeSummary(lead);
    const projectBlock =
      rfqs.length === 1
        ? [
          truncateSmsText(lead.projectTitle, 120).toUpperCase(),
          leadScopeSummary ? `- ${leadScopeSummary}` : null,
        ].filter(Boolean).join('\n')
        : [
          `${rfqs.length} repair projects:`,
          ...rfqs.slice(0, 3).map((rfq) => {
            const scopeSummary = buildScopeSummary(rfq);
            return [
              truncateSmsText(rfq.projectTitle, 90).toUpperCase(),
              scopeSummary ? `- ${scopeSummary}` : null,
            ].filter(Boolean).join('\n');
          }),
          ...(rfqs.length > 3 ? ['Additional projects are included in the link.'] : []),
        ].filter(Boolean).join('\n\n');
    const desiredStart = lead.timeline.desiredStartDate || 'TBD';
    const deadline = submissionDeadlineFor(lead);
    const contactName = normalizeContactName(issuer?.name) || normalizeContactName(lead.clientContact.name);

    return [
      `NEW BID REQUEST · Hauser (hauserhq.com)`,
      contactName
        ? `${contactName} is using Hauser to share a bid request for:`
        : `A homeowner is using Hauser to share a bid request for:`,
      projectBlock,
      `Location: ${propertyAddress}`,
      [`Target start: ${desiredStart}`, `Bids due: ${deadline}`].join('\n'),
      `Full bid request details:\n${url}`,
      `Reply with your site-visit availability and estimate timing. Thanks.`,
      contactName ? `— ${contactName}` : '— Homeowner',
    ].join('\n\n');
  };

  const ensureShareUrl = async () => {
    if (shareUrl) return shareUrl;
    await onBeforeOutreach();
    const token = await createRFQShareToken({ rfqIds: rfqs.map((rfq) => rfq.id) });
    const url = `${window.location.origin}/rfq/share/${token.token}`;
    setShareUrl(url);
    return url;
  };

  const markContacted = async (method: OutreachMethod, vendorsToMark: Vendor[]) => {
    const now = Math.floor(Date.now() / 1000);
    const lead = rfqs[0];
    await Promise.all(
      vendorsToMark.map((vendor) =>
        updateVendorRecord({
          id: vendor.id,
          propertyId,
          data: {
            lastContactedAt: now,
            lastContactedMethod: method,
            lastContactedRfqId: lead?.id,
            lastContactedProjectTitle:
              rfqs.length === 1 ? lead?.projectTitle : `${rfqs.length} RFQ bundle`,
          },
        }),
      ),
    );
    await queryClient.invalidateQueries({ queryKey: ['vendors', propertyId] });
  };

  const guard = () => {
    if (!hasRequiredFields) {
      showAlert('Verify the desired start and deadline before contacting vendors.', 'error');
      return false;
    }
    if (!selectedVendor) {
      showAlert('Select a vendor first.', 'error');
      return false;
    }
    return true;
  };

  const handleSms = async () => {
    if (!guard()) return;
    if (!selectedVendor) return;
    if (!smsPhoneFor(selectedVendor)) {
      showAlert('Selected vendor does not have a phone number.', 'error');
      return;
    }
    setBusyMethod('sms');
    try {
      const url = await ensureShareUrl();
      const body = encodeURIComponent(buildMessage(url));
      window.location.href = `sms:${smsPhoneFor(selectedVendor)}?&body=${body}`;
      await markContacted('sms', [selectedVendor]);
      showAlert(`Prepared SMS for ${selectedVendor.name}.`, 'success');
    } catch {
      showAlert('Could not prepare SMS outreach.', 'error');
    } finally {
      setBusyMethod(null);
    }
  };

  const selectVendor = (vendorId: string) => {
    setHasTouchedSelection(true);
    setSelectedVendorId(vendorId);
  };
  const isHeader = placement === 'header';

  return (
    <Box
      sx={{
        borderTop: isHeader ? 'none' : '1px solid var(--tx-line)',
        px: isHeader ? 0 : { xs: 2, sm: '20px' },
        py: isHeader ? 0 : 2,
        backgroundColor: isHeader ? 'transparent' : '#222',
        width: isHeader ? 'auto' : '100%',
      }}>
      <Button
        onClick={(event) => setVendorMenuAnchor(event.currentTarget)}
        disabled={disabled}
        variant="outlined"
        startIcon={
          isFetchingVendors ? (
            <CircularProgress size={14} sx={{ color: 'inherit' }} />
          ) : (
            <DirectoryIcon />
          )
        }
        sx={{
          borderRadius: '10px',
          borderColor: 'var(--tx-line)',
          color: 'var(--tx-text)',
          backgroundColor: 'var(--tx-fill-2)',
          fontSize: 14,
          fontWeight: 500,
          height: 40,
          minHeight: 40,
          py: 0,
          px: '16px',
          textTransform: 'none',
          '& .MuiButton-startIcon': {
            mr: '8px',
            ml: 0,
          },
          '&:hover': {
            borderColor: 'var(--tx-line)',
            backgroundColor: '#AAA69E',
          },
        }}>
        Select vendor
      </Button>

      <Popover
        anchorEl={vendorMenuAnchor}
        open={Boolean(vendorMenuAnchor)}
        onClose={() => setVendorMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              // MUI sets transform/top/left as INLINE styles on the paper for
              // its own positioning — a plain sx transform loses to that
              // (inline always beats a stylesheet class). !important forces it.
              transform: 'translateY(-10px) !important',
              width: 420,
              maxWidth: 'calc(100vw - 40px)',
              overflow: 'hidden',
              backgroundColor: 'var(--tx-glass-panel)',
              backgroundImage: 'none',
              border: 0,
              borderRadius: '14px',
              color: 'var(--tx-text)',
              boxShadow: '0 22px 56px rgba(0,0,0,0.34)',
            },
          },
        }}>
        <Box sx={{ p: '14px' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: 'var(--tx-text)', fontSize: 14, fontWeight: 600 }}>
                Send RFQ to vendor
              </Typography>
              <Typography sx={{ color: '#8B8780', fontSize: 11, mt: '3px' }}>
                Opens a prefilled text. If the number does not accept SMS, call or share the link instead.
              </Typography>
            </Box>
            <Typography sx={{ color: '#6E6B66', fontSize: 11, flexShrink: 0 }}>
              {sortedVendors.length} matching
            </Typography>
          </Stack>

          <Box
            sx={{
              mt: '12px',
              maxHeight: 260,
              overflowY: 'auto',
              borderRadius: '12px',
              backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
            }}>
            {isFetchingVendors && (
              <Typography sx={{ color: 'var(--tx-muted)', fontSize: 12, p: '14px' }}>
                Loading saved vendors...
              </Typography>
            )}
            {!isFetchingVendors && sortedVendors.length === 0 && (
              <Box sx={{ p: '14px' }}>
                <Typography sx={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 600 }}>
                  No matching vendor saved
                </Typography>
                <Typography sx={{ color: '#8B8780', fontSize: 12, mt: '4px', mb: '12px' }}>
                  Find matching vendors from the directory, then come back to send this RFQ.
                </Typography>
                <Box
                  component="button"
                  onClick={() => {
                    setVendorMenuAnchor(null);
                    setFindingTrade(finderTrade);
                  }}
                  sx={{
                    border: 0,
                    color: 'var(--tx-muted)',
                    backgroundColor: 'var(--tx-fill)',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "'Manrope',sans-serif",
                    borderRadius: '8px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    lineHeight: 'normal',
                  '&:hover': {
                      backgroundColor: 'var(--tx-fill-2)',
                    },
                  }}>
                  Find vendors
                </Box>
              </Box>
            )}
            {!isFetchingVendors &&
              sortedVendors.map((vendor) => {
                const [rating, ...mutedMeta] = vendorMeta(vendor);
                const isSelected = vendor.id === selectedVendorId;
                return (
                  <Box
                    key={vendor.id}
                    component="button"
                    type="button"
                    onClick={() => selectVendor(vendor.id)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      p: '12px',
                      border: 0,
                      borderBottom: '1px solid var(--tx-line)',
                      backgroundColor: isSelected ? 'var(--tx-fill)' : 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                      '&:last-of-type': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.055)' },
                    }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isSelected ? 'var(--tx-fill-2)' : 'var(--tx-fill-3)',
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing="6px" alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: 'var(--tx-text)',
                            fontSize: 13,
                            fontWeight: 600,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {vendor.name}
                        </Typography>
                        {vendor.source === 'google_places' && <GoogleBadge />}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing="6px"
                        alignItems="center"
                        sx={{
                          mt: '4px',
                          minWidth: 0,
                          color: '#7E7A73',
                          fontSize: 11,
                          fontWeight: 500,
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {rating && (
                          <Box component="span" sx={{ color: '#C0FF02', fontWeight: 600 }}>
                            ★ {rating}
                          </Box>
                        )}
                        {mutedMeta.map((item) => (
                          <Box key={item} component="span" sx={{ display: 'inline-flex', gap: '6px' }}>
                            <Box component="span" sx={{ opacity: 0.5 }}>
                              ·
                            </Box>
                            {item}
                          </Box>
                        ))}
                        {!rating && mutedMeta.length === 0 && VENDOR_TRADE_LABELS[vendor.trade]}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
          </Box>

          {sortedVendors.length > 0 && (
            <Button
              onClick={handleSms}
              disabled={disabled || Boolean(busyMethod) || !selectedVendor}
              variant="outlined"
              startIcon={
                busyMethod === 'sms' ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined
              }
              sx={{
                mt: '12px',
                width: '100%',
                borderRadius: '10px',
                border: 0,
                color: 'var(--tx-lime-ink)',
                backgroundColor: 'var(--tx-fill)',
                fontSize: 14,
                fontWeight: 500,
                height: 40,
                minHeight: 40,
                textTransform: 'none',
                '& .MuiButton-startIcon': {
                  mr: '8px',
                  ml: 0,
                },
                '&:hover': {
                  border: 0,
                  backgroundColor: 'var(--tx-fill-2)',
                },
              }}>
              Send via SMS
            </Button>
          )}
        </Box>
      </Popover>
      {findingTrade && propertyId && (
        <VendorFinderDialog
          trade={findingTrade}
          propertyId={propertyId}
          savedVendors={vendors}
          onClose={() => setFindingTrade(null)}
        />
      )}
    </Box>
  );
}
